/**
 * Hero Slideshow behavior.
 * - Autoplay with a real progress-bar timer (pausable, resumable).
 * - Keyboard (arrow keys when focused) + click + swipe navigation.
 * - Pauses autoplay when the hero scrolls out of view or the tab is
 *   backgrounded, so it never "jumps" slides while you weren't looking
 *   and never wastes CPU/battery off-screen.
 * - Parallax is a lightweight rAF-throttled scroll listener, skipped
 *   entirely under prefers-reduced-motion or when disabled via settings.
 */
(function () {
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-hero-slideshow]').forEach(function (root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hs-slide]'));
    if (slides.length === 0) return;

    var autoplayEnabled = root.getAttribute('data-autoplay') === 'true' && slides.length > 1 && !prefersReduced;
    var autoplayMs = parseInt(root.getAttribute('data-autoplay-ms'), 10) || 6000;
    var zoomEnabled = root.getAttribute('data-zoom') === 'true' && !prefersReduced;
    var parallaxEnabled = root.getAttribute('data-parallax') === 'true' && !prefersReduced;

    var currentIndex = 0;
    var timerStart = null;
    var rafId = null;
    var isPausedByVisibility = false;
    var isPausedByHover = false;

    var counterEl = root.querySelector('[data-hs-current]');
    var progressBar = root.querySelector('[data-hs-progress]');
    var prevBtn = root.querySelector('[data-hs-prev]');
    var nextBtn = root.querySelector('[data-hs-next]');

    // Sync the CSS Ken Burns transition duration to the real per-slide time.
    if (zoomEnabled) {
      root.style.setProperty('--hs-zoom-duration', autoplayMs + 'ms');
    }

    function triggerZoom(slideEl) {
      if (!zoomEnabled) return;
      var imgs = slideEl.querySelectorAll('[data-hs-zoom] .hs__img');
      imgs.forEach(function (img) {
        // Reset to scale(1) with no transition, force a reflow so the browser
        // actually registers that "before" state, THEN start the transition
        // on the next frame. Without this reset+reflow step, a slide that's
        // already marked active at first paint (the initial slide) has no
        // "before" state to animate from and just renders pre-zoomed.
        img.style.transition = 'none';
        img.style.transform = 'scale(1)';
        void img.offsetWidth; // force reflow
        requestAnimationFrame(function () {
          img.style.transition = 'transform ' + autoplayMs + 'ms linear';
          img.style.transform = 'scale(1.08)';
        });
      });
    }

    function resetZoom(slideEl) {
      var imgs = slideEl.querySelectorAll('[data-hs-zoom] .hs__img');
      imgs.forEach(function (img) {
        img.style.transition = 'none';
        img.style.transform = 'scale(1)';
      });
    }

    function goTo(index) {
      var next = ((index % slides.length) + slides.length) % slides.length;
      resetZoom(slides[currentIndex]);
      slides[currentIndex].classList.remove('hs__slide--active');
      slides[next].classList.add('hs__slide--active');
      currentIndex = next;
      if (counterEl) counterEl.textContent = currentIndex + 1;
      triggerZoom(slides[next]);
      restartTimer();
    }

    function restartTimer() {
      timerStart = performance.now();
      if (progressBar) {
        progressBar.style.transition = 'none';
        progressBar.style.width = '0%';
        // force reflow so the next transition actually starts from 0%
        void progressBar.offsetWidth;
      }
      if (!autoplayEnabled) return;
      if (progressBar) {
        progressBar.style.transition = 'width ' + autoplayMs + 'ms linear';
        requestAnimationFrame(function () {
          progressBar.style.width = '100%';
        });
      }
    }

    function tick(now) {
      if (autoplayEnabled && !isPausedByVisibility && !isPausedByHover && timerStart !== null) {
        if (now - timerStart >= autoplayMs) {
          goTo(currentIndex + 1);
        }
      }
      rafId = requestAnimationFrame(tick);
    }

    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(currentIndex + 1); });
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(currentIndex - 1); });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') goTo(currentIndex + 1);
      if (e.key === 'ArrowLeft') goTo(currentIndex - 1);
    });

    // Pause autoplay only when hovering the nav controls themselves —
    // pausing on hover over the *entire* hero would mean autoplay stops
    // for almost any desktop visitor, since the hero spans nearly the
    // whole viewport. Narrowing the target to the controls matches actual
    // user intent (interacting with the controls) without silently
    // disabling the ambient motion for everyone else.
    var navEl = root.querySelector('.hs__nav');
    if (navEl) {
      navEl.addEventListener('mouseenter', function () { isPausedByHover = true; });
      navEl.addEventListener('mouseleave', function () { isPausedByHover = false; timerStart = performance.now(); });
    }

    // Swipe support (touch)
    var touchStartX = null;
    root.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 40) {
        goTo(dx < 0 ? currentIndex + 1 : currentIndex - 1);
      }
      touchStartX = null;
    }, { passive: true });

    // Pause when scrolled off-screen or tab hidden — saves battery/CPU and
    // prevents the slide changing invisibly while the visitor is elsewhere.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          isPausedByVisibility = !entry.isIntersecting;
          if (entry.isIntersecting) timerStart = performance.now();
        });
      }, { threshold: 0.2 });
      io.observe(root);
    }
    document.addEventListener('visibilitychange', function () {
      isPausedByVisibility = document.hidden;
      if (!document.hidden) timerStart = performance.now();
    });

    restartTimer();
    rafId = requestAnimationFrame(tick);
    triggerZoom(slides[currentIndex]);

    // ---- Parallax ----
    if (parallaxEnabled) {
      var parallaxEls = root.querySelectorAll('[data-hs-parallax]');
      var ticking = false;

      function applyParallax() {
        var rect = root.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        // progress: -1 (hero fully above viewport) .. 0 (centered) .. 1 (fully below)
        var progress = (rect.top) / vh;
        var offset = progress * -24; // max ~24px shift, subtle by design
        parallaxEls.forEach(function (el) {
          el.style.transform = 'translateY(' + offset.toFixed(1) + 'px)';
        });
        ticking = false;
      }

      window.addEventListener('scroll', function () {
        if (!ticking) {
          window.requestAnimationFrame(applyParallax);
          ticking = true;
        }
      }, { passive: true });

      applyParallax();
    }
  });
})();